﻿import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { Breadcrumbs, VerticalLinkGroup } from "components/navigation";
import { UserAccess, UserHasAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { DefectReporting } from "pages/walkaround-check/shared/DefectReporting";
import { AssetService, LinkService, FleetService } from "services";
import { MotTests } from "pages/asset/mots";
import { SafetyFeatures } from "pages/asset/safety-features";
import { Profile } from "pages/asset/profile";
import { Preferences } from "pages/asset/preferences";
import { Intent, Position, Tooltip } from '@blueprintjs/core';
import { AlertConfirm, NotificationInline, NotificationToaster } from "components/notifications";
import { AssetStatus } from "components/status/AssetStatus";
import { Button, LinkButton } from "components/buttons";
import { ShowHide } from "components/layout";
import { AssociatedAssets } from "pages/asset/associated-assets";
import { AssetProfileHistory } from "pages/asset/move-asset/history";
import { ChecksListing } from "pages/checks";
import classNames from "classnames";
import axios from 'axios';
import assetTypeDisplay from './AssetTypeDisplay.json';
import { CollisionListing } from "pages/collisions/shared";
import { DocumentEntityListing } from "pages/documents/listing";
import { Events, DefectListing } from "pages/asset/events"
import moment from "moment";
import { useQuery } from "hooks/useQuery";
import { SetFiltering } from "state/actions";

export function Asset(props) {
    const query = useQuery();
    const area = query.get("area");
    const params = useParams();
    const id = params.id;

    const areaReduxKey = "assetArea";
    const reduxArea = useSelector(state => state.filters[areaReduxKey]);

    const requiredActions = ["Fleet:View", "FleetPCN:View", "FleetVIS:View"];
    const fleetTachoAction = ["Fleet:View"];
    const usesFleetTacho = UserHasAccess(fleetTachoAction);
    const fleetAction = ["FMS:View"];
    const usesFleet = UserHasAccess(fleetAction);
    const deleteAssetAction = "Internal:Super";
    const badRequestCode = 400;
    const queryParams = new URLSearchParams(window.location.search)
    const alertBreadCrumbs = [{ location: '/alert-type', title: "Alert Types" }, { location: `/alert/${queryParams.get("alertType")}?pageNo=${queryParams.get("pageNo")}&searchTerm=${queryParams.get("searchTerm")}`, title: "Alert List" }, { title: "Profile" }];
    const canAccessPage = UserHasAccess(requiredActions);
    const [asset, setAsset] = useState({});
    const [assetOptions, setAssetOptions] = useState({});
    const [eventOptions, setEventOptions] = useState(null);
    const [loadingData, setLoadingData] = useState(true);
    const [savingData, setSavingData] = useState(false);
    const [assetStatusesMappings, setAssetStatusesMappings] = useState([]);
    const [ownershipAttributeMappings, setOwnershipAttributeMappings] = useState([]);
    const [isDeleteAssetModalOpen, setIsDeleteAssetModalOpen] = useState(false);
    const [isInActiveStatus, setIsInActiveStatus] = useState(false);
    const [isDeletingAsset, setIsDeletingAsset] = useState(false)
    const [assetHasFleetSchedule, setAssetHasFleetSchedule] = useState(false)
    const [eventTypesForAsset, setEventTypesForAsset] = useState([])
    const [defectWarnings, setDefectWarnings] = useState({});
    const [breadCrumbs, setBreadcrumbs] = useState(props.breadcrumbs);
    const profileDataLoadErrorMessage = `Failed to load profile data. Please refresh the page.`;
    const mmvBreadCrumb = [{ location: '/', title: "Home" }, { location: '/asset/manage', title: "Manage Assets" }, { title: "Profile" }];
    const dispatch = useDispatch();
    
    const allRoutes = [
        {
            location: `${LinkService.getVehicleProfileUrl(id, "details")}`,
            title: "Details",
            requiredAction: requiredActions
        },
        {
            location: `${LinkService.getVehicleProfileUrl(id, "preferences")}`,
            title: "Preferences",
            requiredAction: requiredActions
        },
        {
            location: `/asset/${id}/associated-assets`,
            title: "Associated Assets",
            requiredAction: requiredActions
        },
        {
            location: `${window.env.VISION_URL}/#/Fleet/RedirectToProfile?assetId=${id}&tab=inspectionsTab`,
            title: "Inspections",
            externalLink: true,
            requiredAction: ['PlannedMaintenance:View', 'AuditsInspections:View']
        },
        {
            location: `/asset/${id}/events`,
            title: "Events",
            requiredAction: ["FMS:View"]
        },
        {
            location: `/asset/${id}/defects`,
            title: "Defects",
            requiredAction: ["FMS:View", "Checks:View","FleetVIS:View"]
        },
        {
            location: `/asset/${id}/mot-tests`,
            title: "MOT Tests",
            requiredAction: requiredActions,
        },
        {
            location: `${window.env.VISION_URL}/#/Fleet/RedirectToProfile?assetId=${id}&tab=pcnTab`,
            title: "PCN",
            externalLink: true,
            requiredAction: "FleetPCN:View"
        },
        {
            location: `/asset/${id}/checks`,
            title: "Checks",
            requiredAction: "Checks:View",
        },
        {
            location: `/asset/${id}/walkaround-checks`,
            title: "Walkaround Checks - Legacy",
            requiredAction: "WalkaroundCheck:View",
        },
        {
            location: `/asset/${id}/safety-features`,
            title: "Safety Features",
            requiredAction: requiredActions,
        },
        {
            location: `/asset/${id}/collisions`,
            title: "Collisions",
            requiredAction: "Collisions:View",
        },
        {
            location: `/asset/${id}/documents`,
            title: "Documents",
            requiredAction: "Documents:View",
        },
        {
            location: `/asset/${id}/history`,
            title: "History",
            requiredAction: requiredActions
        }
    ];

    useEffect(() => {
        let tempArea = area;

        // we prioritise the area from the query parameter but fallback to the redux area if it has a value. 
        // If none of these are set it should default to the MMV breadcrumb 
        if (!area && reduxArea) {
            tempArea = reduxArea;
        }

        switch(tempArea) {
            case "MMF":
                setBreadcrumbs([{ location: '/', title: "Home" }, { location: '/fleet/manage', title: "Fleet Planner" }, { title: "Profile" }]);
                break;
            case "MMFH":
                setBreadcrumbs([{ location: '/', title: "Home" }, { location: '/fleet/overview', title: "Fleet Overview" }, { title: "Profile" }]);
                break;
            case "TACHO":
                setBreadcrumbs([{ location: '/', title: "Home" }, { location: '/vehicle/manage', title: "Tacho Overview" }, { title: "Profile" }]);
                break;
            case "MAINT":
                setBreadcrumbs([{ location: `${window.env.VISION_URL}/#/Fleet`, title: "Maintenance Overview", externalLink: true }, { title: "Profile" }]);
                break;
            case "PCN":
                setBreadcrumbs([{ location: `${window.env.VISION_URL}/#/Fleet/PcnOverview`, title: "PCN Overview", externalLink: true }, { title: "Profile" }]);
                break;
            case "ER":
                setBreadcrumbs([{ location: "/earned-recognition", title: "Earned Recognition" }, { title: "Profile" }]);
                break;
            case "MMV":
            default:
                setBreadcrumbs(mmvBreadCrumb);
                break;
        }

        dispatch(SetFiltering(areaReduxKey, tempArea));
    }, [area, reduxArea]);

    const routesForUser = allRoutes.filter((x) =>
        x.requiredAction ? UserHasAccess(x.requiredAction) : true
    );

    function ensureValidSubtype(originalAsset, value) {
        const prevAssetTypeId = originalAsset.assetType?.id;
        const incomingAssetTypeId = value.assetType?.id;
        const incomingSubtypeId = value.assetType?.subtypeId;

        // If asset type changed and no new subtype is selected, set to uncategorised
        if (
            prevAssetTypeId !== incomingAssetTypeId &&
            (incomingSubtypeId === null || incomingSubtypeId === undefined)
        ) {
            value.assetType.subtypeName = null;
        }
    }

    function saveAssetProfile(value, reload) {
        setSavingData(true);

        ensureValidSubtype(asset, value);

        AssetService.saveAssetProfile(value).then(() => {
            setAsset(value);
            if (reload) {
                fetchData();
            }
            NotificationToaster.show(Intent.SUCCESS, `Profile updated successfully.`);
        }).catch((error) => {
            if (error?.status === badRequestCode) {
                NotificationToaster.show(Intent.WARNING, error.data);
            } else {
                NotificationToaster.show(Intent.DANGER, "Failed to save profile data. Please try again.");
            }
        }).finally(() => {
            setSavingData(false);
        });
    }
    useEffect(() => {
        if (!eventTypesForAsset) {
            if (assetHasFleetSchedule) {
                setAssetHasFleetSchedule(false);
            }

            return;
        }
        var eventTypes = eventTypesForAsset.filter(e => !e.disabled && e.scheduled);
        if (eventTypes && eventTypes.length > 0) {
            setAssetHasFleetSchedule(true);
        }
    }, [eventTypesForAsset])

    useEffect(() => {
        fetchData();
    }, [id]);

    function fetchData() {
        if (!canAccessPage) {
            return;
        }

        setLoadingData(true);

        let promises = [
            AssetService.getAsset(id),
            AssetService.getAssetOptions()
        ];

        if (usesFleet) {
            promises.push(
                FleetService.getEventTypesForAsset(id, true),
                FleetService.getAssetsWithWarnings([id]),
                FleetService.getEventOptions()
            );
        }

        if (usesFleetTacho) {
            promises.push(AssetService.getUploadSummary(id));
        }

        axios.all(promises)
            .then(responses => {
                const getAssetResponse = responses[0];
                const getAssetOptionsResponse = responses[1];
                const uploadSummaryOrEventTypesForAssetIdx = 2;
                const assetsWithWarningsIdx = 3;
                const eventOptionsIdx = 4;
                const uploadSummaryIdx = 5;

                if (getAssetResponse != null) {
                    const tempAssetType = getAssetOptionsResponse?.assetTypes?.find(at => at.id === getAssetResponse.assetType.id) ?? null;

                    // if the asset's asset type is not VIS only, then we want to get the upload summary
                    if (tempAssetType != null && !tempAssetType.visOnly && usesFleetTacho) {
                        const getUploadSummaryResponse = usesFleet ? responses[uploadSummaryIdx] : responses[uploadSummaryOrEventTypesForAssetIdx];
                        
                        getAssetResponse.lastUploadDate = getUploadSummaryResponse.uploadDate;
                        getAssetResponse.lastActivityDate = getUploadSummaryResponse.lastActivityDate;
                        getAssetResponse.lastDownloadDate = getUploadSummaryResponse.downloadDate;
                    }
                    
                    setAsset(getAssetResponse);
                    setIsInActiveStatus(getAssetResponse.assetLocation.assetStatus.id === 2);
                }

                if (getAssetOptionsResponse != null) {
                    setAssetOptions(getAssetOptionsResponse);
                    setOwnershipAttributeMappings(getAssetOptionsResponse.assetOwnerships.map((x) => {
                        return { "name": x.name, "colours": x.colour, "id": x.id, "className": x.style, "icon": x.icon }
                    }));
                    setAssetStatusesMappings(getAssetOptionsResponse.assetStatuses.map((x) => {
                        return { "name": x.name, "colours": x.colour, "id": x.id }
                    }));
                }

                if (usesFleet) {
                    const getEventTypesForAssetResponse = responses[uploadSummaryOrEventTypesForAssetIdx];
                    const getAssetsWithWarningsResponse = responses[assetsWithWarningsIdx];
                    const eventOptionsResponse = responses[eventOptionsIdx];
                    
                    if (getEventTypesForAssetResponse != null) {
                        setEventTypesForAsset(getEventTypesForAssetResponse);
                    }
                    if (eventOptionsResponse != null) {
                        setEventOptions(eventOptionsResponse);
                    }
                    if (getAssetsWithWarningsResponse != null) {
                        const apiWarnings = getAssetsWithWarningsResponse[0].warnings || [];
                        setDefectWarnings({
                            hasWarnings: apiWarnings.length > 0,
                            hasDangerousWarnings: apiWarnings.some(w => w.dangerous),
                            warnings: apiWarnings.map(w => w.message)
                        });
                    }
                }
            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, profileDataLoadErrorMessage);
            })
            .finally(() => {
                setLoadingData(false);
            });
    }

    function eventSchedulesChanged() {
        FleetService.getEventTypesForAsset(id, true).then((r) => {
            setEventTypesForAsset(r);
        });
    }

    const vorMessage = useMemo(() => {
        const vorStateId = 3;
        const isVOR = asset?.assetLocation?.assetStatus?.id === vorStateId;
        const vorStart = asset?.vorStartDate ? moment(asset.vorStartDate) : null;
        const vorEnd = asset?.vorEndDate ? moment(asset.vorEndDate) : null;
        const today = moment().startOf('day');

        const formattedVorStart = asset?.vorStartDate ? vorStart.format("DD/MM/YYYY") : "";
        const formattedVorEnd = asset?.vorEndDate ? vorEnd.format("DD/MM/YYYY") : "";

        if (vorStart && vorEnd && asset?.eventTypeName) {
            if (vorStart.isAfter(today) && !isVOR) {
                return `Vehicle Off Road Timer: The asset status for this asset is scheduled to be set to VOR on ${formattedVorStart} for its ${asset.eventTypeName}.`;
            } else if (vorStart.isSameOrBefore(today) && isVOR) {
                return `Vehicle Off Road Timer: The asset status for this asset is due to be set back from VOR on ${formattedVorEnd} after its ${asset.eventTypeName}.`;
            }
        }
        return "";
    }, [asset]);

    function getWarningIcon(warning) {
        if (!warning || !warning.hasWarnings) return null;

        const tooltipText = (
            <ul>
                {warning.warnings.map(w => (<li key={w}>{w}</li>))}
            </ul>
        );
        const warningsClass = warning.hasDangerousWarnings ? "text-intent-danger" : "text-intent-warning";

        return (
            <Tooltip position={Position.TOP} content={tooltipText}>
                <i className={`fa fa-warning ${warningsClass}`} />
            </Tooltip>
        );
    }

    function showDeleteAssetModal() {
        setIsDeleteAssetModalOpen(true);
    }

    function hideDeleteAssetModal() {
        setIsDeletingAsset(false);
        setIsDeleteAssetModalOpen(false);
    }

    function onDeleteAsset() {
        setIsDeletingAsset(true);
        AssetService.deleteAsset(id).then(() => {
            NotificationToaster.show(Intent.SUCCESS, `Asset deleted successfully.`);
            window.location.href = "/vehicle/manage";
        }).catch(() => {
            NotificationToaster.show(Intent.DANGER, `Failed to delete asset. Please try again.`);
        }).finally(() => {
            setIsDeletingAsset(false);
        });
    }

    return (
        <UserAccess
            perform={requiredActions}
            yes={() => (
                <Fragment>
                    <div className="row">
                        <Breadcrumbs items={queryParams.get("alertType") === null ? breadCrumbs : alertBreadCrumbs} />
                        <div className={classNames({ "bp6-skeleton": loadingData }, 'inline-items', 'spacer-bottom-medium')}>
                            <AssetStatus statusId={asset?.assetLocation?.assetStatus?.id} ownershipId={asset?.assetLocation?.assetOwnership?.id} isLoading={loadingData} statusAttributeMappings={assetStatusesMappings} ownershipAttributeMappings={ownershipAttributeMappings} tooltipPosition={Position.RIGHT} />
                                {getWarningIcon(defectWarnings)}
                            <h1 className="no-margin">{asset?.registration}{asset?.assetLocation?.fleetNumber ? ` / ${asset?.assetLocation?.fleetNumber}` : ''}</h1>
                        </div>
                        <NotificationInline
                            id="asset-profile-vehicle-offroad-timer-notification"
                            loading={loadingData}
                            show={!!vorMessage}
                            text={vorMessage}
                            intent="warning"
                            allowClose={false}
                        />
                        <div className="inline-items inline-items-space space-large">
                            <ShowHide
                                evaluator={reduxArea === "TACHO" || usesFleetTacho}
                                show={(<LinkButton icon="calendar" intent="primary" text={reduxArea === "TACHO" || reduxArea === "MAINT" || reduxArea === "MMV" || reduxArea === "MMF" ? "Tacho Overview Calendar" : "Calendar"} href={`/vehicle/manage/${id}/calendar`} className="spacer-bottom" />)}
                            />
                            <UserAccess
                                perform={deleteAssetAction}
                                yes={() => (
                                    <ShowHide
                                        evaluator={isInActiveStatus}
                                        show={
                                            <Button icon="trash" intent="delete" text="Delete Asset" onClick={showDeleteAssetModal} className="spacer-bottom" />
                                        }
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <AlertConfirm
                        title="Delete Asset"
                        isOpen={isDeleteAssetModalOpen}
                        onConfirm={onDeleteAsset}
                        onCancel={hideDeleteAssetModal}
                        confirmButtonIntent="delete"
                        confirmButtonText="Delete Asset"
                        disabled={isDeletingAsset}
                    >
                        <p>Are you sure you want to delete this asset?</p>
                    </AlertConfirm>

                    <div className="layout-cols">
                        <div className="col side-col">
                            <div className="row">
                                <VerticalLinkGroup links={routesForUser} exactRouteMatch={false} />
                            </div>
                        </div>
                        <div className="col content-col">
                            <Routes>
                                <Route path="" element={<Navigate to={`/asset/${id}/details`} replace />} />
                                <Route path="details" element={<Profile assetProfile={asset} assetTypeDisplay={assetTypeDisplay} assetOptions={assetOptions} loading={loadingData} saving={savingData} saveProfile={saveAssetProfile} key={id} assetHasFleetSchedule={assetHasFleetSchedule} />} />
                                <Route path="preferences" element={<Preferences assetProfile={asset} saveProfile={saveAssetProfile} assetTypeDisplay={assetTypeDisplay} assetOptions={assetOptions} loading={loadingData} saving={savingData} />} />
                                <Route path="associated-assets" element={<AssociatedAssets assetProfile={asset} assetTypeDisplay={assetTypeDisplay} assetOptions={assetOptions} assetStatusesMappings={assetStatusesMappings} ownershipAttributeMappings={ownershipAttributeMappings} loading={loadingData} saving={savingData} saveProfile={saveAssetProfile} key={id} />} />
                                <Route path="checks" element={<ChecksListing entityId={id} withBreadcrumbs={false} withCheckSettingsButton={false} withManageTemplatesButton={false} withLocationsFilter={false} id={id} route={"asset"} category="Asset" />} />
                                <Route path="walkaround-checks" element={<DefectReporting id={id} route={"asset"} sortName={"employeeFullName"} headerName={"Completed by"} />} />
                                <Route path="events/*" element={<Events assetId={id} asset={asset} eventTypesForAsset={eventTypesForAsset} eventTypesChanged={eventSchedulesChanged} />} />
                                <Route path="defects/*" element={<DefectListing asset={asset} eventTypesForAsset={eventTypesForAsset} eventTypesChanged={eventSchedulesChanged} loading={loadingData} pageState="PROFILE" mappedAssetOptions={{ ownershipAttributeMappings, assetStatusesMappings, assetTypes: assetOptions.assetTypes }} eventOptions={eventOptions} />} />
                                <Route path="mot-tests" element={<MotTests asset={asset} assetId={id} key={id} />} />
                                <Route path="safety-features" element={<SafetyFeatures assetId={id} key={id} />} />
                                <Route path="collisions" element={<CollisionListing profileType="asset" entityId={id} />} />
                                <Route path="documents" element={<div className="row"><h2>Documents</h2><DocumentEntityListing entityType="Asset" entityId={id} showTitle={false} filterName={'asset-documents'} /></div>} />
                                <Route path="history" element={<AssetProfileHistory assetId={id} key={id} />} />
                            </Routes>
                        </div>
                    </div>
                </Fragment>
            )}
            no={() => <PageRestricted />}
        />
    );
}
